import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { ProjectInvoice } from '@/types'

interface DeleteInvoiceDialogProps {
  invoice: ProjectInvoice | null
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * SOLY-INV-001 — confirmation dialog for deleting the last invoice.
 * Surfaces the fiscal risk (Soly cannot verify whether it was sent / VAT-declared), has no
 * reason field (immediate error correction), and points to the credit note for the cases where
 * deletion is not the right path. User-facing copy stays in French.
 */
export function DeleteInvoiceDialog({ invoice, isDeleting, onConfirm, onCancel }: DeleteInvoiceDialogProps) {
  if (!invoice) return null

  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent data-testid="invoice-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer la facture {invoice.number} ?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                À ne faire{' '}
                <span className="font-medium text-foreground">
                  que si cette facture n'a pas été envoyée au client ni déclarée à la TVA
                </span>{' '}
                — Soly ne peut pas le vérifier.
              </p>
              <p>
                Son numéro redeviendra disponible pour la prochaine facture.{' '}
                <span className="font-medium text-foreground">Suppression définitive.</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Déjà envoyée ou déclarée ? Utilisez plutôt une note de crédit.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isDeleting} data-testid="invoice-delete-cancel-btn">
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault() // keep the dialog open during the async call (loading state)
              onConfirm()
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="invoice-delete-confirm-btn"
          >
            {isDeleting ? 'Suppression…' : 'Supprimer définitivement'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
